import { StdListCell, StdListCheckbox, StdListRow } from 'components/StdList';
import Immutable from 'immutable';
import React, { CSSProperties } from 'react';
import { Submittal, SubmittalId } from 'records/Submittal';
import { useTranslator, useDateFormatter } from '@bluebeam/react-i18n';

interface Props {
  submittal: Submittal;
  openSubmittal: SubmittalId | null;
  selectedSubmittals: Immutable.Set<SubmittalId>;
  onSubmittalClick: (submittal: Submittal) => void;
  handleSubmittalChecked: (submittal: Submittal) => void;
  style: CSSProperties;
}

const SubmittalLogRow: React.FC<Props> = (props) => {
  const dueDate = (): number => {
    const { submittal } = props;
    const subRequested = submittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      submittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';
    const notRequested = submittal.status.kind === 'SUB_NOT_YET_REQUESTED';
    const pendingGCReview = submittal.status.kind === 'SUB_PENDING_GC_APPROVAL';

    if ((subRequested || notRequested) && submittal.duefromsub > 0) {
      return submittal.duefromsub;
    }

    if ((subDesignReview || pendingGCReview) && submittal.duefromreviewer) {
      return submittal.duefromreviewer;
    }

    return 0;
  };

  const shouldShowDueDate = (): boolean => {
    const { submittal } = props;
    const subRequested = submittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      submittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';
    const notRequested = submittal.status.kind === 'SUB_NOT_YET_REQUESTED';
    const pendingGCReview = submittal.status.kind === 'SUB_PENDING_GC_APPROVAL';

    return subRequested || notRequested || subDesignReview || pendingGCReview;
  };

  const {
    submittal,
    openSubmittal,
    selectedSubmittals,
    onSubmittalClick,
    handleSubmittalChecked,
    style,
  } = props;

  const openSubmittalStyle =
    openSubmittal === submittal.id
      ? { backgroundColor: 'rgba(16, 168, 235, 0.1)', ...style }
      : style;

  const isApproved = submittal.status.kind === 'SUB_APPROVED';

  const rowId = `sub-log-row-${submittal.id}`;
  const { translate } = useTranslator(['statusLabels']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'long',
  });

  const reviewerResponse: `${string}:${string}` =
    `submittalsPage:reviewerResponse.${submittal.reviewerresponse.kind}` as `${string}:${string}`;

  const categoryType: `${string}:${string}` =
    `submittalsPage:categories.${submittal.category.kind}` as `${string}:${string}`;

  const statusType: `${string}:${string}` =
    `statusLabels:submittals.${submittal.status.kind}` as `${string}:${string}`;

  return (
    <StdListRow
      id={`sub-log-row-${submittal.id}`}
      onClick={() => onSubmittalClick(submittal)}
      style={openSubmittalStyle}
    >
      <StdListCheckbox
        rowId={`sub-log-row-${submittal.id}`}
        disabled={false}
        onChange={() => handleSubmittalChecked(submittal)}
        checked={selectedSubmittals.has(submittal.id)}
      />
      <StdListCell id={`${rowId}-cell-sub-number`}>
        {`${submittal.specsectionnumber}-${submittal.sectioncount
          .toString()
          .padStart(3, '0')}`}
      </StdListCell>
      <StdListCell id={`${rowId}-cell-specsectionnumber`}>
        {submittal.specsectionnumber}
      </StdListCell>
      <StdListCell id={`${rowId}-cell-specsectionname`}>
        {submittal.specsectionname}
      </StdListCell>
      <StdListCell id={`${rowId}-cell-description`}>
        {submittal.description}
      </StdListCell>
      <StdListCell id={`${rowId}-cell-category`}>
        {translate(categoryType)}
      </StdListCell>
      <StdListCell id={`${rowId}-cell-status`}>
        {isApproved
          ? `${translate(statusType)} - ${translate(reviewerResponse)}`
          : `${translate(statusType)}`}
      </StdListCell>
      {/* TODO: Looks like duedate is kinda "dynamic". need to figure out the logic for it. */}
      <StdListCell id={`${rowId}-cell-nex-duedate`}>
        {shouldShowDueDate() && dueDate()
          ? dateFormatter?.format(new Date(dueDate() * 1000))
          : translate('submittalsPage:noDueDate')}
      </StdListCell>
    </StdListRow>
  );
};

function shouldUpdate(
  prevProps: {
    submittal: Submittal;
    selectedSubmittals: Immutable.Set<SubmittalId>;
    openSubmittal: SubmittalId | null;
  },
  nextProps: {
    submittal: Submittal;
    selectedSubmittals: Immutable.Set<SubmittalId>;
    openSubmittal: SubmittalId | null;
  }
): boolean {
  return (
    prevProps.submittal !== nextProps.submittal ||
    prevProps.selectedSubmittals !== nextProps.selectedSubmittals ||
    prevProps.openSubmittal !== nextProps.openSubmittal
  );
}

const memoizedSubmittalLogRow = React.memo(SubmittalLogRow, shouldUpdate);

export default memoizedSubmittalLogRow;
